/**
 * A layout only knows how to turn a single control (input/select/textarea)
 * into finished Bootstrap markup. It holds no DOM logic and no state beyond
 * label visibility, which keeps it trivially testable in isolation.
 */
class FieldLayout {
	constructor({ labelVisible = true } = {}) {
		this.labelVisible = labelVisible;
	}

	/** Class for above-labels and <legend>s. */
	get labelClass() {
		return this.labelVisible ? 'form-label' : 'form-label visually-hidden sr-only';
	}

	/** Is a visible label rendered somewhere? Drives the <select> empty-option prompt. */
	get showsLabel() {
		return this.labelVisible;
	}

	/** Placeholder baked into a control. `override` wins when provided. */
	placeholder(label, override = '') {
		return override;
	}

	/** Text shown in the field's own <label>. */
	labelFor(label, override = '') {
		return label;
	}

	/** Default "label above" markup. Also the fallback for non-floatable fields. */
	wrapField({ id, label, control }) {
		return `
			<label for="${id}" class="${this.labelClass}">${label}</label>
			<div class="input-group has-validation">
				${control}
			</div>
		`;
	}

	wrapColumn({ id, label, control, colClass }) {
		return `
			<div class="col-12 ${colClass}">
				<label for="${id}" class="${this.labelClass}">${label}</label>
				<div class="input-group has-validation">
					${control}
				</div>
			</div>
		`;
	}
}

/** form.below — label rendered above the control. */
export class BelowLayout extends FieldLayout {}

/** form.text_inside_input_field — label floats inside the control. */
export class FloatingLayout extends FieldLayout {
	// Floating always shows a label, regardless of labelVisible.
	get showsLabel() {
		return true;
	}

	// The floating effect needs a placeholder; fall back to the label text.
	placeholder(label, override = '') {
		return override || label;
	}

	// A custom (data-attribute) placeholder replaces the floating label text.
	labelFor(label, override = '') {
		return override || label;
	}

	wrapField({ id, label, control, canFloat = true }) {
		if (!canFloat) return super.wrapField({ id, label, control });

		return `
			<div class="input-group has-validation">
				<div class="form-floating">
					${control}
					<label for="${id}">${label}</label>
				</div>
			</div>
		`;
	}

	wrapColumn({ id, label, control, colClass, canFloat = true }) {
		if (!canFloat) return super.wrapColumn({ id, label, control, colClass });

		return `
			<div class="col-12 ${colClass}">
				<div class="input-group has-validation">
					<div class="form-floating">
						${control}
						<label for="${id}">${label}</label>
					</div>
				</div>
			</div>
		`;
	}
}
